// Version check utility to detect stale bundles and auto-refresh

const BUILD_VERSION_KEY = 'app_build_version';
const VERSION_CHECK_INTERVAL = 5 * 60 * 1000; // Check every 5 minutes

// Generate a unique build ID based on current deployment
// This will change with each new deployment
const getCurrentBuildVersion = (): string => {
  // Use a combination of factors that change per deployment
  // The script tags and their hashes change with each build
  const scripts = document.querySelectorAll('script[src*="/assets/"]');
  const scriptHashes = Array.from(scripts).map(s => s.getAttribute('src')).filter(Boolean).sort().join('|');
  
  // Simple hash function
  let hash = 0;
  for (let i = 0; i < scriptHashes.length; i++) {
    const char = scriptHashes.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32bit integer
  }
  
  return hash.toString(36);
};

// Store the current version on app load
export const initVersionCheck = (): void => {
  const currentVersion = getCurrentBuildVersion();
  const storedVersion = localStorage.getItem(BUILD_VERSION_KEY);
  
  if (!storedVersion) {
    // First visit, store the version
    localStorage.setItem(BUILD_VERSION_KEY, currentVersion);
    return;
  }
  
  if (storedVersion !== currentVersion) {
    // Version mismatch - new deployment detected
    console.log('[VersionCheck] New version detected, updating stored version');
    localStorage.setItem(BUILD_VERSION_KEY, currentVersion);
    
    // Clear any chunk error flags since we have fresh code
    sessionStorage.removeItem('chunk_error_reload');
    sessionStorage.removeItem('chunk_fallback_reload');
    sessionStorage.removeItem('page_reloaded_for_chunk');
  }
};

// Check for version updates in the background
export const startVersionMonitor = (): (() => void) => {
  let intervalId: NodeJS.Timeout | null = null;
  
  const checkForUpdates = async () => {
    try {
      // Fetch the index.html to check for new script hashes
      const response = await fetch('/', {
        method: 'HEAD',
        cache: 'no-store',
        headers: {
          'Cache-Control': 'no-cache',
          'Pragma': 'no-cache'
        }
      });
      
      // If the server returns a different ETag or Last-Modified, there might be updates
      const etag = response.headers.get('etag');
      const lastModified = response.headers.get('last-modified');
      
      const storedEtag = localStorage.getItem('app_etag');
      const storedLastModified = localStorage.getItem('app_last_modified');
      
      if (etag) {
        if (storedEtag && storedEtag !== etag) {
          console.log('[VersionCheck] Server version changed (ETag), prompting refresh');
          showUpdateNotification();
        }
        localStorage.setItem('app_etag', etag);
      }
      
      if (lastModified) {
        if (storedLastModified && storedLastModified !== lastModified) {
          console.log('[VersionCheck] Server version changed (Last-Modified), prompting refresh');
          showUpdateNotification();
        }
        localStorage.setItem('app_last_modified', lastModified);
      }
    } catch (error) {
      // Silently fail - network issues shouldn't break the app
      console.debug('[VersionCheck] Update check failed:', error);
    }
  };
  
  // Start periodic checks
  intervalId = setInterval(checkForUpdates, VERSION_CHECK_INTERVAL);
  
  // Also check when tab becomes visible
  const handleVisibilityChange = () => {
    if (document.visibilityState === 'visible') {
      checkForUpdates();
    }
  };
  
  document.addEventListener('visibilitychange', handleVisibilityChange);
  
  // Cleanup function
  return () => {
    if (intervalId) {
      clearInterval(intervalId);
    }
    document.removeEventListener('visibilitychange', handleVisibilityChange);
  };
};

// Show a subtle notification that an update is available
const showUpdateNotification = () => {
  // Check if we've already shown this notification in this session
  if (sessionStorage.getItem('update_notification_shown') === 'true') {
    return;
  }
  
  sessionStorage.setItem('update_notification_shown', 'true');
  
  // Create and show a toast-like notification
  const notification = document.createElement('div');
  notification.id = 'version-update-notification';
  notification.style.cssText = `
    position: fixed;
    bottom: 80px;
    left: 50%;
    transform: translateX(-50%);
    background: hsl(var(--primary));
    color: hsl(var(--primary-foreground));
    padding: 12px 20px;
    border-radius: 8px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    z-index: 9999;
    display: flex;
    align-items: center;
    gap: 12px;
    font-family: system-ui, -apple-system, sans-serif;
    font-size: 14px;
    cursor: pointer;
    transition: opacity 0.3s, transform 0.3s;
  `;
  
  notification.innerHTML = `
    <span>A new version is available</span>
    <button style="
      background: hsl(var(--primary-foreground));
      color: hsl(var(--primary));
      border: none;
      padding: 6px 12px;
      border-radius: 4px;
      font-size: 13px;
      font-weight: 500;
      cursor: pointer;
    ">Refresh</button>
  `;
  
  document.body.appendChild(notification);
  
  // Handle click on refresh button
  notification.querySelector('button')?.addEventListener('click', () => {
    window.location.reload();
  });
  
  // Auto-dismiss after 10 seconds
  setTimeout(() => {
    notification.style.opacity = '0';
    notification.style.transform = 'translateX(-50%) translateY(10px)';
    setTimeout(() => notification.remove(), 300);
  }, 10000);
};

// Export a hook-friendly version
export const useVersionCheck = () => {
  // Initialize on first import
  if (typeof window !== 'undefined') {
    initVersionCheck();
  }
};
